import React, {useState, useRef, useEffect, useMemo} from 'react';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {useAppSelector} from '@/shared/hooks/redux';
import {selectCurrentPatient} from '@/slices/patientSlice';
import {useSubscription} from '@/modules/patient-dashboard/hooks/useTestsQuery';
import HeaderMembershipPoints from './HeaderMembershipPoints';
import {isOrganizationUser, isProviderOrOrganizationUser, isUserAdmin} from '@/shared/utils/tokenUtils';
import {buildAdminOrigin} from '@/shared/utils/adminHost';
import {ThemedLogo} from '@/shared/themed/ThemedLogo';
import {useTenant, type OrganizationFeatures} from '@/shared/hooks/useTenant';
import {GOODHUE_OHD_ORG_ID, readOhdOrgId} from '@/shared/constants/ohd';
import Icon, {type IconName} from '@/shared/components/Icon';
import {useCart} from '@/modules/shop/context/CartContext';

// ── Nav item definitions ────────────────────────────────────────────

interface NavItem {
    label: string;
    to: string;
    icon: IconName;
    /** External link (opens in new tab) */
    external?: boolean;
    /** Hidden item (rendered but display:none — kept for future use) */
    hidden?: boolean;
}

const patientNavItems: NavItem[] = [
    {label: 'Dashboard', to: '/', icon: 'house'},
    {label: 'Lab Results', to: '/results', icon: 'vial'},
    // Points at the public store for now (Derrick, 2026-08-11); the internal
    // /shop routes stay live for BioMatch checkout and direct navigation.
    {label: 'Shop Tests', to: 'https://mylabsdirect.com/', icon: 'cart-shopping', external: true},
    {label: 'Subscriptions', to: '/subscriptions', icon: 'calendar'},
    // Hidden while kit registration is broken (Derrick, 2026-08-12); the
    // /test-kits routes stay live for direct navigation.
    {label: 'Test Kits', to: '/test-kits', icon: 'box-open', hidden: true},
    {label: 'Support', to: '/support', icon: 'phone'},
];

const incentivesNavItem: NavItem = {label: 'Incentives', to: '/incentives', icon: 'award'};

const providerNavItems: NavItem[] = [
    {label: 'Console', to: '/', icon: 'house'},
    {label: 'Order Tests', to: '/provider/order-tests', icon: 'flask'},
    {label: 'Support', to: '/support', icon: 'phone'},
];

function getNavItems(pathname: string, features: OrganizationFeatures | undefined): NavItem[] {
    if (pathname.startsWith('/provider')) return providerNavItems;
    if (readOhdOrgId(features) === GOODHUE_OHD_ORG_ID) {
        // Insert "Incentives" between Dashboard and Lab Results.
        return [patientNavItems[0], incentivesNavItem, ...patientNavItems.slice(1)];
    }
    return patientNavItems;
}

// ── Component ───────────────────────────────────────────────────────

const buildAdminUrl = (): string => {
    if (typeof window === 'undefined') return '/admin';
    return `${buildAdminOrigin(window.location)}/admin`;
};

interface HeaderProps {
    rightAction?: React.ReactNode;
}

/** Patient cart control used outside the shop routes as well as inside them. */
const HeaderCartButton: React.FC = () => {
    const {itemCount, toggleDrawer} = useCart();

    return (
        <button
            type="button"
            onClick={toggleDrawer}
            aria-label={`Open cart${itemCount > 0 ? `, ${itemCount} ${itemCount === 1 ? 'item' : 'items'}` : ''}`}
            className="relative p-2 text-mld-menu-text transition-colors hover:text-mld-primary">
            <Icon name="cart-shopping" className="h-5 w-5" />
            {itemCount > 0 && (
                <span className="absolute right-0 top-0 flex h-5 min-w-5 items-center justify-center rounded-full bg-mld-tertiary px-1 text-[10px] font-bold leading-none text-white">
                    {itemCount > 99 ? '99+' : itemCount}
                </span>
            )}
        </button>
    );
};

const Header: React.FC<HeaderProps> = ({rightAction}) => {
    const {user, logout} = useAuth();
    const currentPatient = useAppSelector(selectCurrentPatient);
    const isSubscribed = useSubscription();
    const location = useLocation();
    const navigate = useNavigate();
    const {organization} = useTenant();
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const mobileMenuRef = useRef<HTMLDivElement>(null);
    const adminUrl = useMemo(buildAdminUrl, []);

    const navItems = getNavItems(location.pathname, organization?.features);

    // Close menus when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setUserMenuOpen(false);
            }
            if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
                setMobileMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, []);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleAdminNavigate = () => {
        setUserMenuOpen(false);
        if (typeof window === 'undefined') {
            navigate('/admin');
            return;
        }
        if (adminUrl.startsWith(window.location.origin)) {
            navigate('/admin');
            return;
        }
        window.location.assign(adminUrl);
    };

    const isActive = (path: string) => location.pathname === path;

    const isAdmin = isUserAdmin(user?.token);
    const isPatient = !isAdmin && !isOrganizationUser(user?.token) && !isProviderOrOrganizationUser(user?.token);
    const userFirstName = (currentPatient?.firstName || user?.userInfo?.firstName || '').trim();
    const userLastName = (currentPatient?.lastName || user?.userInfo?.lastName || '').trim();
    const userInitials = (() => {
        const first = userFirstName.charAt(0);
        const last = userLastName.charAt(0);
        const combined = `${first}${last}`.toUpperCase();
        if (combined.trim()) return combined;
        const fallback = (user?.userInfo?.email || user?.username || '').trim().charAt(0).toUpperCase();
        return fallback || '?';
    })();

    return (
        <>
            <header className="bg-mld-menu-background shadow-sm">
                <div className="mx-auto w-full max-w-[1760px] px-4 sm:px-6 xl:px-10 py-4 flex justify-between items-center">
                    {/* Left side - Logo and desktop nav */}
                    <div className="flex items-center">
                        <Link to="/" className="mr-6 lg:mr-10">
                            <ThemedLogo className="h-8" />
                        </Link>

                        {/* Desktop Navigation */}
                        <nav className="hidden lg:flex">
                            <ul className="flex space-x-8">
                                {navItems.map((item) => (
                                    <li key={item.to} className={item.hidden ? 'hidden' : undefined}>
                                        {item.external ? (
                                            <a
                                                href={item.to}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center space-x-2 text-sm font-medium text-mld-menu-text hover:text-mld-primary transition-colors">
                                                <Icon name={item.icon} className="h-4 w-4" />
                                                <span>{item.label}</span>
                                            </a>
                                        ) : (
                                            <Link
                                                to={item.to}
                                                className={`flex items-center space-x-2 text-sm font-medium transition-colors ${
                                                    isActive(item.to)
                                                        ? 'text-mld-primary'
                                                        : 'text-mld-menu-text hover:text-mld-primary'
                                                }`}>
                                                <Icon name={item.icon} className="h-4 w-4" />
                                                <span>{item.label}</span>
                                            </Link>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </div>

                    {/* Right side - Mobile menu button + User menu */}
                    <div className="flex items-center space-x-2">
                        {rightAction ? (
                            <div className="hidden items-center sm:flex">{rightAction}</div>
                        ) : isPatient ? (
                            <div className="flex items-center">
                                <HeaderCartButton />
                            </div>
                        ) : null}
                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="lg:hidden p-2 text-mld-menu-text hover:text-mld-primary transition-colors"
                            aria-label="Toggle mobile menu">
                            {mobileMenuOpen ? (
                                <Icon name="xmark" className="h-6 w-6" />
                            ) : (
                                <Icon name="bars" className="h-6 w-6" />
                            )}
                        </button>

                        {/* User Menu */}
                        <div className="relative" ref={menuRef}>
                            <button
                                onClick={() => setUserMenuOpen(!userMenuOpen)}
                                className="flex items-center space-x-2 lg:space-x-3 text-mld-menu-text hover:text-mld-primary transition-colors">
                                <div
                                    className="flex h-7 w-7 lg:h-8 lg:w-8 items-center justify-center rounded-full bg-mld-primary text-[11px] font-semibold uppercase text-white"
                                    aria-label={`User initials ${userInitials}`}>
                                    {userInitials}
                                </div>
                                <span className="hidden sm:block font-semibold text-sm">
                                    {(() => {
                                        const rawName =
                                            currentPatient?.firstName || user?.userInfo?.firstName || 'My Account';
                                        return rawName.charAt(0).toUpperCase() + rawName.slice(1).toLowerCase();
                                    })()}
                                </span>
                                <Icon
                                    name="chevron-down"
                                    className={`hidden sm:block h-4 w-4 transition-transform ${
                                        userMenuOpen ? 'rotate-180' : ''
                                    }`}
                                />
                            </button>

                            {/* User Dropdown Menu */}
                            {userMenuOpen && (
                                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                                    <Link
                                        to="/settings"
                                        className="flex items-center px-4 py-2 text-sm text-mld-menu-text hover:bg-gray-50 transition-colors"
                                        onClick={() => setUserMenuOpen(false)}>
                                        <Icon name="gear" className="h-4 w-4 mr-2" />
                                        Settings
                                    </Link>
                                    {isAdmin && (
                                        <button
                                            type="button"
                                            className="w-full flex items-center px-4 py-2 text-sm text-mld-menu-text hover:bg-gray-50 transition-colors text-left"
                                            onClick={handleAdminNavigate}>
                                            <Icon name="lock" className="h-4 w-4 mr-2" />
                                            Admin Dashboard
                                        </button>
                                    )}
                                    <hr className="my-2 border-gray-200" />
                                    <button
                                        onClick={handleLogout}
                                        className="w-full flex items-center text-left px-4 py-2 text-sm text-mld-danger hover:bg-gray-50 transition-colors">
                                        <Icon name="arrow-right-from-bracket" className="h-4 w-4 mr-2" />
                                        Logout
                                    </button>
                                </div>
                            )}
                        </div>

                        <HeaderMembershipPoints isSubscribed={isSubscribed} />
                    </div>
                </div>
            </header>

            {/* Mobile Navigation Menu */}
            {mobileMenuOpen && (
                <div
                    className="lg:hidden bg-mld-menu-background border-t border-gray-200 shadow-lg"
                    ref={mobileMenuRef}>
                    <nav className="px-4 py-4">
                        <ul className="space-y-2">
                            {navItems.map((item) => (
                                <li key={item.to} className={item.hidden ? 'hidden' : undefined}>
                                    {item.external ? (
                                        <a
                                            href={item.to}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium text-mld-menu-text hover:text-mld-primary hover:bg-gray-50 transition-colors"
                                            onClick={() => setMobileMenuOpen(false)}>
                                            <Icon name={item.icon} className="h-5 w-5" />
                                            <span>{item.label}</span>
                                        </a>
                                    ) : (
                                        <Link
                                            to={item.to}
                                            className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                                isActive(item.to)
                                                    ? 'text-mld-primary bg-mld-primary/10'
                                                    : 'text-mld-menu-text hover:text-mld-primary hover:bg-gray-50'
                                            }`}
                                            onClick={() => setMobileMenuOpen(false)}>
                                            <Icon name={item.icon} className="h-5 w-5" />
                                            <span>{item.label}</span>
                                        </Link>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </nav>
                </div>
            )}
        </>
    );
};

export default Header;
